import { Box, Flex } from '@radix-ui/themes';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonTabs } from 'components/common/tabs';
import { HitterProfileHeader } from 'components/sections/hitter-library/profile/header';
import { HitterProfileReports } from 'components/sections/hitter-library/profile/reports';
import { HitterProfileSummary } from 'components/sections/hitter-library/profile/summary';
import useHitterProfileStore from 'components/sections/hitter-library/profile/use-hitter-profile-store';
import { HittersContext } from 'contexts/hitters.context';
import { IVerboseComponent } from 'interfaces/i-components';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { paths } from 'routes/paths';
import { useAuthStore } from 'stores/use-auth-store';

enum TabKey {
  Summary = 'Summary',
  Reports = 'Reports',
}

export const HitterProfile = (props: IVerboseComponent) => {
  const navigate = useNavigate();

  const isInGame = useAuthStore((s) => s.isInGame);

  const { hitters } = useContext(HittersContext);
  const routeParams = useParams<{
    hitterID: string;
  }>();

  const hitter = useMemo(
    () => hitters.find((h) => h._id === routeParams.hitterID),
    [hitters, routeParams.hitterID]
  );

  useEffect(() => {
    useHitterProfileStore.getState().setHitter(hitter);
    useHitterProfileStore
      .getState()
      .mergeHitsFilter({ hitterID: hitter?._id ?? '' });
    return () => {
      useHitterProfileStore.getState().resetFilters();
    };
  }, [hitter]);

  useEffect(() => {
    if (!isInGame) {
      return;
    }
    navigate(paths.gameInProgress);
  }, [isInGame, navigate]);

  const [activeTab, setActiveTab] = useState(TabKey.Summary);

  return (
    <ErrorBoundary identifier={props.identifier}>
      <Flex direction="column" gap="3">
        <HitterProfileHeader identifier="HitterProfileHeader" />

        <Box>
          <CommonTabs
            value={activeTab}
            onValueChange={(value) => setActiveTab(value as TabKey)}
            tabs={[
              {
                value: TabKey.Summary,
                label: 'common.summary',
                content: <HitterProfileSummary />,
              },
              {
                value: TabKey.Reports,
                label: 'hitters.hitting-reports',
                content: (
                  <HitterProfileReports
                    refetchSummaries={() =>
                      useHitterProfileStore
                        .getState()
                        .mergeHitsFilter({ hitterID: hitter?._id ?? '' })
                    }
                  />
                ),
              },
            ]}
            gap="0"
          />
        </Box>
      </Flex>
    </ErrorBoundary>
  );
};
